import { useCallback } from "react";

// Custom hook for CSV download functionality
const useCSVDownload = () => {
  // Function to convert timestamp to readable date
  const convertTimestamp = useCallback((timestamp) => {
    if (!timestamp || !timestamp._seconds) return "";
    return new Date(timestamp._seconds * 1000).toISOString();
  }, []);

  // Function to convert JSON to CSV
  const convertToCSV = useCallback(
    (data) => {
      if (!data || data.length === 0) return "";

      // Flatten the data structure
      const flattenedData = data.map((item) => ({
        batteryId: item.batteryId || "",
        locationFrom: item.location?.from || "",
        locationTo: item.location?.to || "",
        createdAt: convertTimestamp(item.createdAt),
        updatedAt: convertTimestamp(item.updatedAt),
        initiatedBy: item.initiatedBy || "",
        remark: item.remark || "",
        comment: item.comment || "",
        statusValue: item.status?.value || "",
        statusChangedBy: item.status?.changedBy || "",
      }));

      // Get headers
      const headers = Object.keys(flattenedData[0]);

      // Create CSV content
      const csvContent = [
        headers.join(","), // Header row
        ...flattenedData.map((row) =>
          headers
            .map((header) => {
              const value = row[header];
              // Escape commas and quotes in values
              if (
                typeof value === "string" &&
                (value.includes(",") || value.includes('"'))
              ) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value;
            })
            .join(",")
        ),
      ].join("\n");

      return csvContent;
    },
    [convertTimestamp]
  );

  // Function to download CSV
  const downloadCSV = useCallback(
    (data, filename = "battery_transfers.csv") => {
      const csvContent = convertToCSV(data);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        URL.revokeObjectURL(url);
      }
    },
    [convertToCSV]
  );

  // Generic CSV download function for any data structure
  const downloadGenericCSV = useCallback(
    (data, filename = "data.csv", customMapper = null) => {
      if (!data || data.length === 0) return;

      let processedData = data;

      // Apply custom mapper if provided
      if (customMapper && typeof customMapper === "function") {
        processedData = data.map(customMapper);
      }

      // Get headers from first object
      const headers = Object.keys(processedData[0]);

      // Create CSV content
      const csvContent = [
        headers.join(","), // Header row
        ...processedData.map((row) =>
          headers
            .map((header) => {
              const value = row[header];
              // Escape commas and quotes in values
              if (
                typeof value === "string" &&
                (value.includes(",") || value.includes('"'))
              ) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value;
            })
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        URL.revokeObjectURL(url);
      }
    },
    []
  );

  return {
    downloadCSV,
    downloadGenericCSV,
    convertToCSV,
    convertTimestamp,
  };
};

export default useCSVDownload;
